import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { GraduationCapIcon, ShieldCheckIcon, MailIcon, ArrowRightIcon, CheckIcon, LockIcon, ZapIcon, ClipboardListIcon, SparklesIcon, SchoolIcon, AwardIcon } from 'lucide-react';
import { getBridgeTemplate } from '../data/bridgeTemplates';
import { useMetaTags } from '../hooks/useMetaTags';
import { V9BridgeLayout } from '../components/V9BridgeLayout';
import { TrackingNoticeLinks } from '../components/TrackingNoticeLinks';
import { getPublisherParams } from '../utils/publisherParams';
import { degreeLevelOptions, areaStudyOptions, programOptions, getDefaultProgram } from '../data/filterOptions';
const NAVY = '#1a2332';
const NAVY_ACCENT = '#2a374a';
const ORANGE = '#e8594f';
const ORANGE_HOVER = '#d64a41';
// Module-level lock so the email-remarketing beacon fires at most once per
// unique lead for the lifetime of the page session. A component-scoped ref
// resets on every remount (React StrictMode double-mount, route remounts,
// HMR), which caused the beacon to fire repeatedly. A module-level Set keyed
// by the request URL survives all remounts while still allowing a genuinely
// different lead (different URL) to fire its own single beacon.
const firedRemarketingUrls = new Set<string>();
export function FindDegreesBridgePage() {
  const {
    templateId
  } = useParams<{
    templateId: string;
  }>();
  const template = getBridgeTemplate(templateId);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // v6 selector state
  const [degreeLevel, setDegreeLevel] = useState<number | ''>('');
  const [areaOfStudy, setAreaOfStudy] = useState<number | ''>('');
  const [program, setProgram] = useState<number | ''>('');
  // v8 — Clicks Search carousel state. When `showClicksCarousel` is
  // true, the page swaps the selector form for an in-page carousel
  // that displays one school at a time from the Clicks API response.
  const [showClicksCarousel, setShowClicksCarousel] = useState(false);
  const [clicksResults, setClicksResults] = useState<any[]>([]);
  const [clicksLoading, setClicksLoading] = useState(false);
  const [clicksIndex, setClicksIndex] = useState(0);
  const [clicksError, setClicksError] = useState('');
  useMetaTags({
    title: `${template.headlineHighlight} | FindOnlineDegrees`,
    description: template.subhead,
    // Bridge templates are paid traffic landing pages — keep them out of
    // the organic index so they don't compete with content pages.
    robots: 'noindex, follow'
  });
  // Email remarketing beacon — fires once on landing whenever the URL
  // carries an email AND a first and/or last name. Forwards the lead to
  // Media Matchers' email-remarketing endpoint so the contact can be
  // enrolled in downstream nurture campaigns. Fire-and-forget (no UI).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const email = (params.get('email') || '').trim();
    const firstName = (params.get('firstName') || '').trim();
    const lastName = (params.get('lastName') || '').trim();
    // Require an email AND at least one name part before firing.
    if (!email || !firstName && !lastName) return;
    const remarketingParams = new URLSearchParams();
    // Publisher identification: prefer mPubGroupID, else mPubID+mPubCampaignID
    // from the URL; if all are blank fall back to the partner-supplied
    // mPubID/mPubCampaignID default pair.
    Object.entries(getPublisherParams(location.search, {
      mPubID: '83768',
      mPubCampaignID: '37138'
    })).forEach(([k, v]) => remarketingParams.set(k, v));
    // Map remaining values straight from the URL — only append the ones
    // that actually have a value so we don't send empty params.
    const passthrough: Record<string, string> = {
      firstName,
      lastName,
      email,
      sub1: params.get('sub1') || '',
      sub2: params.get('sub2') || '',
      sub3: params.get('sub3') || '',
      userIP: params.get('userIP') || '',
      zipCode: params.get('zipCode') || '',
      mProgram: params.get('mProgram') || '',
      startTimeframe: params.get('startTimeframe') || ''
    };
    Object.entries(passthrough).forEach(([k, v]) => {
      if (v) remarketingParams.set(k, v);
    });
    const url = `https://data.media-matchers.com/email-remarketing?${remarketingParams.toString()}`;
    // Guard against duplicate fires across remounts: only one beacon per
    // unique lead URL for the lifetime of this page session.
    if (firedRemarketingUrls.has(url)) return;
    firedRemarketingUrls.add(url);
    console.log('[Email Remarketing] PING (GET):', url);
    // Use default `cors` mode (not no-cors) so the response body is
    // readable. The request still reaches the server either way; cors mode
    // additionally exposes the JSON to JS/DevTools IF the endpoint returns
    // Access-Control-Allow-Origin. If it doesn't, the read is blocked
    // (the ping still lands) — open the logged URL in a browser tab to see
    // the raw JSON directly.
    fetch(url, {
      method: 'GET',
      keepalive: true
    }).then(async res => {
      const text = await res.text();
      try {
        const json = JSON.parse(text);
        console.log('[Email Remarketing] response JSON:', json);
      } catch {
        console.log('[Email Remarketing] response (raw):', text);
      }
    }).catch(err => {
      console.warn('[Email Remarketing] response not readable (likely CORS). The ping still reached the server. Open the PING URL in a new tab to see the JSON. Error:', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    params.set('email', email);
    navigate(`/?${params.toString()}`);
  };
  const handleCtaClick = () => {
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    // healthcare-v1: same single-button CTA layout as v2, but on click
    // swap the page for the in-page Clicks carousel with degreeInterest=5
    // (Master's) and mProgram=500 (Healthcare) hardcoded.
    if (templateId === 'healthcare-v1') {
      setDegreeLevel(5);
      setProgram(500);
      setShowClicksCarousel(true);
      callV8ClicksAPI(5, 500);
      return;
    }
    // v2: open the Media Matchers API as a pop-under and keep the user on
    // the listings page in the foreground tab.
    if (templateId === 'v2') {
      const externalParams = new URLSearchParams({
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || '',
        mediaType: '2',
        redirect: '1'
      });
      Object.entries(getPublisherParams(location.search, {
        mPubGroupID: 'PCGP4731148320'
      })).forEach(([k, v]) => externalParams.set(k, v));
      const externalUrl = `https://api.media-matchers.com/search?${externalParams.toString()}`;
      try {
        const popunder = window.open(externalUrl, '_blank');
        if (popunder) {
          popunder.blur();
          window.focus();
        }
      } catch {
        // Popup blocked or failed — proceed with main navigation regardless.
      }
      navigate(`/?${params.toString()}`);
      return;
    }
    // v5: navigate to the colleges page (preserve any sub params)
    if (templateId === 'v5') {
      navigate(`/colleges?${params.toString()}`);
      return;
    }
    // v3 only: redirect to findonlinedegrees.org request-information page
    if (templateId === 'v3') {
      const externalParams = new URLSearchParams({
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || ''
      });
      Object.entries(getPublisherParams(location.search, {
        mPubGroupID: 'PCGP4731148320'
      })).forEach(([k, v]) => externalParams.set(k, v));
      window.location.href = `https://findonlinedegrees.org/request-information?${externalParams.toString()}`;
      return;
    }
    navigate(`/?${params.toString()}`);
  };
  const isCta = template.variant === 'cta';
  const isSelector = template.variant === 'selector';
  const availablePrograms = areaOfStudy ? programOptions[areaOfStudy as number] || [] : [];
  // Fetch Clicks Search API and render results inline as carousel.
  // Used by both v8 (selector form) and healthcare-v1 (single CTA).
  // Accepts explicit overrides because state setters are async — the
  // caller may have just set state in the same tick.
  const callV8ClicksAPI = async (degreeInterestOverride?: number | '', mProgramOverride?: number | '') => {
    setClicksLoading(true);
    setClicksError('');
    setClicksResults([]);
    const di = degreeInterestOverride !== undefined ? degreeInterestOverride : degreeLevel;
    const mp = mProgramOverride !== undefined ? mProgramOverride : program;
    const params = new URLSearchParams(location.search);
    const searchParams = new URLSearchParams({
      degreeInterest: di === '' ? '' : String(di),
      mProgram: mp === '' ? '' : String(mp),
      sub1: params.get('sub1') || '',
      sub2: params.get('sub2') || '',
      sub3: params.get('sub3') || ''
    });
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    const url = `https://api.media-matchers.com/search?${searchParams.toString()}`;
    console.log('[v8 Clicks API] PING (GET):', url);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: any = await response.json();
      console.log('[v8 Clicks API] response:', data);
      const schools = data?.result || data?.results || data?.schools || data?.data || (Array.isArray(data) ? data : []) || [];
      setClicksResults(Array.isArray(schools) ? schools : []);
      if (!schools || schools.length === 0) {
        setClicksError('No matching schools found.');
      }
    } catch (err) {
      clearTimeout(timeout);
      console.error('[v8 Clicks API] error:', err);
      setClicksError('Unable to load matching schools. Please try again.');
    } finally {
      setClicksLoading(false);
    }
  };
  const handleV8ClickThrough = () => {
    const school: any = clicksResults[clicksIndex];
    const clickURL = school?.clickURL || school?.clickUrl || school?.url || school?.link;
    if (clickURL) {
      try {
        window.open(clickURL, '_blank', 'noopener,noreferrer');
      } catch (err) {
        console.warn('[v8 Clicks] failed to open clickURL:', err);
      }
    }
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      const params = new URLSearchParams(location.search);
      params.set('degreeInterest', String(degreeLevel));
      params.set('mProgram', String(program));
      navigate(`/?${params.toString()}`);
    }
  };
  const handleV8Skip = () => {
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      const params = new URLSearchParams(location.search);
      params.set('degreeInterest', String(degreeLevel));
      params.set('mProgram', String(program));
      navigate(`/?${params.toString()}`);
    }
  };
  const handleSelectorSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!degreeLevel || !areaOfStudy || !program) return;
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    params.set('degreeInterest', String(degreeLevel));
    params.set('mProgram', String(program));
    // v8 and financial-aid-v1/v2: swap to in-page Clicks API carousel.
    if (templateId === 'v8' || templateId === 'financial-aid-v1' || templateId === 'financial-aid-v2') {
      setShowClicksCarousel(true);
      callV8ClicksAPI();
      return;
    }
    // v7: redirect to external Media Matchers search API,
    // and also open the home page (with the same selections) as a pop-under.
    if (templateId === 'v7') {
      const externalParams = new URLSearchParams({
        degreeInterest: String(degreeLevel),
        mProgram: String(program),
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || '',
        redirect: '1'
      });
      Object.entries(getPublisherParams(location.search, {
        mPubGroupID: 'PCGP4731148320'
      })).forEach(([k, v]) => externalParams.set(k, v));
      const externalUrl = `https://api.media-matchers.com/search?${externalParams.toString()}`;
      // Pop-under: open the API URL in a new tab and drop it behind,
      // then navigate the current window to the home/listings page so the
      // user stays on our site.
      try {
        const popunder = window.open(externalUrl, '_blank');
        if (popunder) {
          popunder.blur();
          window.focus();
        }
      } catch {
        // Popup blocked or failed — proceed with main navigation regardless.
      }
      navigate(`/?${params.toString()}`);
      return;
    }
    navigate(`/?${params.toString()}`);
  };
  // v9 layout override
  if (template.layout === 'hq') {
    return <V9BridgeLayout template={template} onCtaClick={handleCtaClick} submitting={submitting} data-id="element-1602" data-mp="ixfvqglv5eqf" />;
  }
  // v8: when the carousel is active, render it in place of the
  // selector page. Keeps the slim header for brand consistency.
  if (showClicksCarousel) {
    const currentClick: any = clicksResults[clicksIndex];
    const cSchoolName = currentClick?.mBrandName || currentClick?.brandName || currentClick?.name || 'Partner School';
    const cSchoolLogo = currentClick?.imageURL || currentClick?.imageUrl || currentClick?.logoUrl;
    const cHeadline = currentClick?.headContent || '';
    const cSchoolHighlight = currentClick?.schoolHighlight || '';
    const cAboutText = currentClick?.aboutBrand || currentClick?.aboutCollege || currentClick?.shortDescription || '';
    const cAccreditation = currentClick?.accreditation || '';
    const cRichHtml = currentClick?.bodyContent || '';
    const cClickURL = currentClick?.clickURL || currentClick?.clickUrl || currentClick?.url || currentClick?.link;
    return <div className="min-h-screen flex flex-col font-sans text-gray-900 bg-gray-50" data-id="element-1603" data-mp="216uww97h1pe0">
        <header className="w-full relative overflow-hidden" style={{
        backgroundColor: NAVY
      }} data-id="element-1604" data-mp="259hr2grm7med">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1605" data-mp="3lr9ql2ldfcoy"></div>
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between relative z-10" data-id="element-1606" data-mp="3e1x4y4brukc">
            <div className="flex items-center gap-2.5" data-id="element-1607" data-mp="3upboh99t5p80">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
              backgroundColor: ORANGE
            }} data-id="element-1608" data-mp="d9vhv4s76ud7">
                <GraduationCapIcon className="w-5 h-5 text-white" data-id="element-1609" data-mp="majy8652d6t9" />
              </div>
              <div className="leading-tight" data-id="element-1610" data-mp="hc9zxmo1lq3f">
                <div className="font-bold text-white text-base" data-id="element-1611" data-mp="hafe864y5tya">
                  FindOnlineDegrees
                </div>
                <div className="text-[10px] text-gray-400 tracking-widest" data-id="element-1612" data-mp="2j3wkp6hccorg">
                  YOUR MATCHES
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-gray-300 text-xs" data-id="element-1613" data-mp="n2l1hakht0rj">
              <LockIcon className="w-3.5 h-3.5 text-white" data-id="element-1614" data-mp="s0mjrta3gw1y" />
              <span data-id="element-1615" data-mp="wz6820c5ujxh">SSL Secured</span>
            </div>
          </div>
        </header>
        <main className="flex-grow" data-id="element-1616" data-mp="x1ahsywqqd5o">
          <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-1617" data-mp="110rotwrtficy">
            {clicksLoading ? <div className="text-center py-20" data-id="element-1618" data-mp="160e56f2xxepz">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8594f] mb-4" data-id="element-1619" data-mp="15xi7mrv08s22"></div>
                <p className="text-xl text-gray-700 font-semibold" data-id="element-1620" data-mp="1fpfi1we6vfzq">
                  Finding your matches...
                </p>
              </div> : clicksError || clicksResults.length === 0 ? <div className="text-center py-20 bg-white rounded-xl border border-gray-200 shadow-sm" data-id="element-1621" data-mp="1oqdqqrzkobon">
                <p className="text-xl text-gray-700 font-semibold mb-4" data-id="element-1622" data-mp="1tonlj1hdxa9q">
                  {clicksError || 'No matches found.'}
                </p>
                <button onClick={() => {
              const params = new URLSearchParams(location.search);
              params.set('degreeInterest', String(degreeLevel));
              params.set('mProgram', String(program));
              navigate(`/?${params.toString()}`);
            }} className="px-6 py-3 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg transition-all" data-id="element-1623" data-mp="3e8iz8jp72j9a">
                  Continue to Listings →
                </button>
              </div> : <>
                <div className="text-center mb-8" data-id="element-1624" data-mp="3o3ufx7k5y0jo">
                  <p className="text-xs uppercase tracking-widest text-[#e8594f] font-bold mb-1" data-id="element-1625" data-mp="3j6u92ufhotri">
                    Your Matches
                  </p>
                  <p className="text-sm text-gray-600 font-semibold" data-id="element-1626" data-mp="khn33ve0pima">
                    School {clicksIndex + 1} of {clicksResults.length}
                  </p>
                  <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden max-w-xs mx-auto" data-id="element-1627" data-mp="3k759wp9kpsxp">
                    <div className="h-full bg-[#e8594f] transition-all duration-300" style={{
                  width: `${(clicksIndex + 1) / clicksResults.length * 100}%`
                }} data-id="element-1628" data-mp="3oz6q2m9v43t0" />
                  </div>
                </div>
                <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10" data-id="element-1629" data-mp="6t1txnmhiup8">
                  {cSchoolHighlight && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold border border-amber-200 shadow-sm" data-id="element-1630" data-mp="3txhzwx05qeb8">
                      <span aria-hidden="true" data-id="element-1631" data-mp="2iib5osacxbdl">✨</span>
                      {cSchoolHighlight}
                    </span>}
                  <div className="text-center mb-8" data-id="element-1632" data-mp="2mkg0vvggeg2c">
                    {cSchoolLogo && <img src={cSchoolLogo} alt={cSchoolName} className="h-28 md:h-32 mx-auto mb-6 object-contain" data-id="element-1633" data-mp="2eyu8dlqge0yn" />}
                    {cAboutText && <p className="text-base text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-1634" data-mp="2jyp1zx1m54ne">
                        {cAboutText}
                      </p>}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2" data-id="element-1635" data-mp="1rb2uidp9lfv9">
                    {cSchoolName}
                  </h2>
                  {cHeadline && cHeadline.trim() !== cSchoolName.trim() && <p className="text-base md:text-lg text-[#e8594f] font-semibold text-center mb-3" data-id="element-1636" data-mp="2175yzq7l9qld">
                      {cHeadline}
                    </p>}
                  {cAccreditation && <p className="text-xs text-gray-500 text-center mb-5" data-id="element-1637" data-mp="18hyzanweeuyj">
                      Accredited by {cAccreditation}
                    </p>}
                  {cRichHtml && <div className="prose prose-sm max-w-none text-gray-700 mb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1" dangerouslySetInnerHTML={{
                __html: cRichHtml
              }} data-id="element-1638" data-mp="2lq9hkhh978f4" />}
                  <div className="space-y-4" data-id="element-1639" data-mp="34j3rgx00sach">
                    <button onClick={handleV8ClickThrough} disabled={!cClickURL} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1640" data-mp="34ic5mdw85qze">
                      Visit {cSchoolName} →
                    </button>
                    <button onClick={handleV8Skip} className="w-full py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg border-2 border-gray-300 transition-all" data-id="element-1641" data-mp="19ysb5qdl8u8p">
                      Skip This School
                    </button>
                  </div>
                </div>
              </>}
          </div>
        </main>
        {/* Always rendered: every page running the pixel needs its tracking
            notice and opt-out links (Meta Business Tools Terms §3.b). */}
        <footer className="w-full border-t border-gray-200 bg-white mt-auto" data-id="element-1642" data-mp="vb7by7ju3f62">
            <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-500" data-id="element-1643" data-mp="hbfz4a5204tn">
              <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs mb-4 font-medium" data-id="element-1644" data-mp="ma94tge3mrj9">
                <a href="/ca-notice" className="hover:text-[#e8594f] transition-colors" data-id="element-1645" data-mp="2bop2dk70au3g">
                  CA Consumers: Do Not Sell My Info
                </a>
                <a href="/terms" className="hover:text-[#e8594f] transition-colors" data-id="element-1646" data-mp="2e9wvt1ddv9l9">
                  Terms
                </a>
                <a href="/privacy-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1647" data-mp="258oz2nqimgta">
                  Privacy Policy
                </a>
              </nav>
              <p className="text-xs mb-4" data-id="element-1648" data-mp="2xvruv83t4dfu">
                © {new Date().getFullYear()} FindOnlineDegrees. All rights
                reserved.
              </p>
              {template.footerDisclosure && <p className="text-[10px] leading-relaxed max-w-5xl mx-auto text-justify" data-id="element-1649" data-mp="2o4ots6avnygk">
                  {template.footerDisclosure}
                </p>}
              <div className="text-[10px] leading-relaxed max-w-3xl mx-auto pt-3" data-id="element-1650" data-mp="3gonbn1kwjkz9">
                <TrackingNoticeLinks tone="light" data-id="element-1651" data-mp="3bvaxjldumwkr" />
              </div>
            </div>
        </footer>
      </div>;
  }
  return <div className="min-h-screen flex flex-col font-sans text-gray-900 bg-gray-100 bg-cover bg-center bg-no-repeat bg-fixed" style={{
    backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/3azvozn9iRJDmqfhLE2fmB/Background_hero.png")'
  }} data-id="element-1652" data-mp="3j9lasiz7b75k">
      {/* Slim Header — matches site nav style. Hidden on mobile to maximize
                                                above-the-fold form visibility. */}
      <header className="w-full relative overflow-hidden" style={{
      backgroundColor: NAVY
    }} data-id="element-1653" data-mp="az9yzpvsavu4">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1654" data-mp="fra5602g6kz5"></div>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between relative z-10" data-id="element-1655" data-mp="pp7rgi0qy7p4">
          <div className="flex items-center gap-2.5" data-id="element-1656" data-mp="tq9s93abart7">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
            backgroundColor: ORANGE
          }} data-id="element-1657" data-mp="tqt1zwpt69w5">
              <GraduationCapIcon className="w-5 h-5 text-white" data-id="element-1658" data-mp="1mftehb9l58y9" />
            </div>
            <div className="leading-tight" data-id="element-1659" data-mp="206oioa9531vi">
              <div className="font-bold text-white text-base" data-id="element-1660" data-mp="209kd3ols38f7">
                FindOnlineDegrees
              </div>
              <div className="text-[10px] text-gray-400 tracking-widest" data-id="element-1661" data-mp="18ga7ilsig3nj">
                DEGREE MATCHING SERVICE
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-gray-300 text-xs" data-id="element-1662" data-mp="13jjt02wctg48">
            <LockIcon className="w-3.5 h-3.5 text-white" data-id="element-1663" data-mp="1hi1fjxaiwmkk" />
            <span data-id="element-1664" data-mp="1cif2bnup36br">SSL Secured</span>
          </div>
        </div>
      </header>

      {/* Main Content — Two-Column, vertically centered */}
      <section className="flex-grow flex items-center" data-id="element-1665" data-mp="2sv3witgjbe59">
        <div className="w-full max-w-7xl mx-auto px-4 py-3 md:py-16 lg:py-20" data-id="element-1666" data-mp="2nxkd7f4zcb6g">
          {/* Mobile-only eyebrow + H1 — sits ABOVE the form so the user
           reads the value prop, then immediately sees the form. */}
          <div className="lg:hidden text-center mb-5" data-id="element-1667" data-mp="30gz9kb8t9879">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4 text-[11px] font-bold tracking-widest" style={{
            backgroundColor: '#4A49E5',
            color: '#ffffff'
          }} data-id="element-1668" data-mp="30dl8r7fsp71x">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: '#ffffff'
            }} data-id="element-1669" data-mp="3f5lxv2gf1aam"></span>
              {template.eyebrow}
            </div>
            <h1 className="font-bold leading-[1.1] text-gray-900" style={{
            fontSize: 'clamp(1.75rem, 7vw, 2.5rem)'
          }} data-id="element-1670" data-mp="3adu7arx9f4kh">
              {template.headlineLead}{' '}
              <span style={{
              color: ORANGE
            }} data-id="element-1671" data-mp="6q27qcb1k7zs">
                {template.headlineHighlight}
              </span>{' '}
              {template.headlineTrail}
            </h1>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-10 lg:gap-16 items-center" data-id="element-1672" data-mp="6mterm0bz9b6">
            {/* LEFT: Headline — appears BELOW the form on mobile, beside it on desktop */}
            <div className="text-center lg:text-left order-2 lg:order-1" data-id="element-1673" data-mp="2nasalsv2q68">
              {/* Desktop-only eyebrow + H1 (mobile renders these above the grid) */}
              <div className="hidden lg:block" data-id="element-1674" data-mp="3ozrhu1sf6i9k">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-5 text-[11px] font-bold tracking-widest" style={{
                backgroundColor: '#4A49E5',
                color: '#ffffff'
              }} data-id="element-1675" data-mp="3oyzmms2qimjt">
                  <span className="w-1.5 h-1.5 rounded-full" style={{
                  backgroundColor: '#ffffff'
                }} data-id="element-1676" data-mp="361yfp1pmecd8"></span>
                  {template.eyebrow}
                </div>

                <h1 className="font-bold leading-[1.1] mb-5 text-gray-900" style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.25rem)'
              }} data-id="element-1677" data-mp="1qh5xodbwhx8z">
                  {template.headlineLead}{' '}
                  <span style={{
                  color: ORANGE
                }} data-id="element-1678" data-mp="12ufxlhj44cr0">
                    {template.headlineHighlight}
                  </span>{' '}
                  {template.headlineTrail}
                </h1>
              </div>

              {template.financialAid ? <div className="space-y-6 max-w-xl mx-auto lg:mx-0 text-left" data-id="element-1679" data-mp="1gm4c1rgu3db5">
                  <div className="bg-white rounded-xl p-6 border-l-4 shadow-sm" style={{
                borderColor: ORANGE
              }} data-id="element-1680" data-mp="j4w37c3rv72i">
                    <p className="text-gray-600 font-medium mb-1" data-id="element-1681" data-mp="3dmu3g82gy8ip">
                      {template.financialAid.grantCaption.split(template.financialAid.grantAmount)[0]}
                    </p>
                    <div className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-1" data-id="element-1682" data-mp="3ikdfr2flwb80">
                      {template.financialAid.grantAmount}
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-1683" data-mp="3rlk0jnpag872">
                      {template.financialAid.grantCaption.split(template.financialAid.grantAmount)[1]}
                    </p>
                  </div>

                  {template.financialAid.earningsStat && <div className="bg-gray-50 rounded-xl p-5 border border-gray-200" data-id="element-1684" data-mp="e7yswh24zemv">
                      <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-2" data-id="element-1685" data-mp="xti83bz27095">
                        {template.financialAid.earningsStat}
                      </p>
                      {template.financialAid.earningsDisclaimer && <p className="text-xs text-gray-400 leading-tight" data-id="element-1686" data-mp="os0t5wxe14m1">
                          {template.financialAid.earningsDisclaimer}
                        </p>}
                    </div>}

                  {(template.financialAid.weCanHeadline || template.financialAid.exploreHeadline) && <div className="grid sm:grid-cols-2 gap-4" data-id="element-1687" data-mp="55cmkn06l789">
                      {template.financialAid.weCanHeadline && <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-id="element-1688" data-mp="a47d6b4e402x">
                          <h3 className="font-bold text-gray-900 mb-2" data-id="element-1689" data-mp="3s9srru614mt7">
                            {template.financialAid.weCanHeadline}
                          </h3>
                          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1690" data-mp="2k74b8j652djo">
                            {template.financialAid.weCanBody}
                          </p>
                        </div>}
                      {template.financialAid.exploreHeadline && <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-id="element-1691" data-mp="2y6n8ggxew4h7">
                          <h3 className="font-bold text-gray-900 mb-2" data-id="element-1692" data-mp="335q97p6czojd">
                            {template.financialAid.exploreHeadline}
                          </h3>
                          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1693" data-mp="3bxiihwjncrmd">
                            {template.financialAid.exploreBody}
                          </p>
                        </div>}
                    </div>}
                </div> : <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0" data-id="element-1694" data-mp="3qujbe3dkqsmf">
                  {template.subhead}
                </p>}

              {/* Stats strip — site theme */}
            </div>

            {/* RIGHT: Form — appears ABOVE the supporting text on mobile */}
            <aside className="w-full max-w-md mx-auto lg:max-w-none lg:mx-0 order-1 lg:order-2" data-id="element-1695" data-mp="15ab4mm7phsb6">
              {/* Step indicator */}
              <div className="flex items-center gap-3 mb-5 px-1" data-id="element-1696" data-mp="10bzx4i4vvtw4">
                <div className="flex items-center gap-2" data-id="element-1697" data-mp="1xwvv7kgcy0do">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white" style={{
                  backgroundColor: ORANGE
                }} data-id="element-1698" data-mp="1syuakv3a9t03">
                    <CheckIcon className="w-4 h-4" data-id="element-1699" data-mp="1j7zmdl6h8jc0" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700" data-id="element-1700" data-mp="2goygxa7uw6us">
                    Profile Complete
                  </span>
                </div>
                <div className="flex-grow h-px bg-gray-300" data-id="element-1701" data-mp="2bvlr4wrfxu0g"></div>
                <div className="flex items-center gap-2" data-id="element-1702" data-mp="a17mami04p75">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse" style={{
                  backgroundColor: NAVY,
                  boxShadow: `0 0 0 4px rgba(232,89,79,0.15)`
                }} data-id="element-1703" data-mp="ez9a1kqdhcoy">
                    2
                  </div>
                  <span className="text-xs font-semibold" style={{
                  color: NAVY
                }} data-id="element-1704" data-mp="3nhfagia0v8lp">
                    Confirm Email
                  </span>
                </div>
              </div>

              {isSelector ? <form onSubmit={handleSelectorSubmit} className="bg-white rounded-2xl p-7 md:p-9 shadow-xl border border-gray-200" data-id="element-1705" data-mp="1bprrk6e73p9f">
                  <div className="text-center mb-6" data-id="element-1706" data-mp="1gm8qzwquhq1t">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-id="element-1707" data-mp="317f7bbgd60qw">
                      {template.cardTitle}
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-1708" data-mp="3awgpn8adsu81">
                      {template.cardSubtitle}
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-1709" data-mp="3645ndys64wcb">
                    <div data-id="element-1710" data-mp="3jx54243oswlg">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1711" data-mp="3jwc2rinhyzap">
                        Degree Level <span className="text-red-500" data-id="element-1712" data-mp="3eytqbia9u1xy">*</span>
                      </label>
                      <select value={degreeLevel} onChange={e => setDegreeLevel(e.target.value ? Number(e.target.value) : '')} required className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1713" data-mp="3tvktm2n2fxsu">
                        <option value="" data-id="element-1714" data-mp="3g9nbypz6ynuo">-- Please Select --</option>
                        {degreeLevelOptions.filter(o => o.value !== 5).map(option => <option key={option.value} value={option.value} data-id="element-1715" data-mp="3cs9gd0psyv0a">
                              {option.label}
                            </option>)}
                      </select>
                    </div>

                    <div data-id="element-1716" data-mp="33pi26t5jll4y">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1717" data-mp="33sw460aansbv">
                        Area of Study <span className="text-red-500" data-id="element-1718" data-mp="2yub1lu7kkwk9">*</span>
                      </label>
                      <select value={areaOfStudy} onChange={e => {
                    const v = e.target.value ? Number(e.target.value) : '';
                    setAreaOfStudy(v);
                    setProgram('');
                  }} required className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1719" data-mp="2tuyhvibzw5lw">
                        <option value="" data-id="element-1720" data-mp="25eak4sksissu">-- Please Select --</option>
                        {areaStudyOptions.map(option => <option key={option.value} value={option.value} data-id="element-1721" data-mp="2y5anr2f7lujz">
                            {option.label}
                          </option>)}
                      </select>
                    </div>

                    <div data-id="element-1722" data-mp="rfh4nz3uortq">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1723" data-mp="repa8rlzp051">
                        Program <span className="text-red-500" data-id="element-1724" data-mp="mhfdeqw9tm76">*</span>
                      </label>
                      <select value={program} onChange={e => setProgram(e.target.value ? Number(e.target.value) : '')} required disabled={!areaOfStudy} className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1725" data-mp="115sa5q36n92q">
                        <option value="" data-id="element-1726" data-mp="9xs2zzkkzdtl">
                          {areaOfStudy ? '-- Please Select --' : 'Select an area of study first'}
                        </option>
                        {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-1727" data-mp="2gqkq88xcde4c">
                            {option.label}
                          </option>)}
                      </select>
                    </div>

                    <button type="submit" disabled={submitting || !degreeLevel || !areaOfStudy || !program} className="w-full flex items-center justify-center gap-2 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed" style={{
                  backgroundColor: ORANGE
                }} onMouseEnter={e => {
                  if (!(e.currentTarget as HTMLButtonElement).disabled) {
                    ;
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
                  }
                }} onMouseLeave={e => {
                  ;
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
                }} data-id="element-1728" data-mp="12vm8qjmx2b65">
                      <span data-id="element-1729" data-mp="to40rua0w7lp">{submitting ? 'Loading…' : template.ctaLabel}</span>
                      <ArrowRightIcon className="w-4 h-4" data-id="element-1730" data-mp="op0uk6ib3vc5" />
                    </button>

                    <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-1.5" data-id="element-1731" data-mp="jr8z52igdr37">
                      <LockIcon className="w-3.5 h-3.5" data-id="element-1732" data-mp="fs9znxfhqmj5" />
                      Your information is secure and confidential
                    </p>
                  </div>
                </form> : isCta ? <div className="bg-white rounded-2xl p-8 md:p-10 shadow-xl border border-gray-200 text-center" data-id="element-1733" data-mp="11aukwdv6g4b">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1734" data-mp="3s7c8dcp7rntn">
                    {template.cardHeadline || template.cardTitle}
                  </h2>
                  <p className="text-base font-semibold mb-7" style={{
                color: ORANGE
              }} data-id="element-1735" data-mp="3o8cvn5x8qmrn">
                    {template.cardSubcopy || template.cardSubtitle}
                  </p>

                  <button type="button" onClick={handleCtaClick} disabled={submitting} className="inline-flex items-center justify-center gap-2 text-white font-bold py-4 px-10 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-70" style={{
                backgroundColor: ORANGE
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
              }} data-id="element-1736" data-mp="2cpfniafiptcs">
                    <span data-id="element-1737" data-mp="u5rjyhq7owx3">{submitting ? 'Loading…' : template.ctaLabel}</span>
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1738" data-mp="17wmjv4iqg3oj" />
                  </button>

                  {template.cardFootnote && <p className="text-sm text-gray-600 mt-7 leading-relaxed max-w-sm mx-auto" data-id="element-1739" data-mp="12zcwrvitg43v">
                      {template.cardFootnote}
                    </p>}
                </div> : <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-7 md:p-9 shadow-xl border border-gray-200" data-id="element-1740" data-mp="p76hscreg360">
                  <label htmlFor="bridge-email" className="block text-sm font-bold mb-3 text-gray-900" data-id="element-1741" data-mp="ka6bq1ujv0ew">
                    Where should we send your matches?
                  </label>

                  <div className="relative mb-4" data-id="element-1742" data-mp="3dkdhbiu9qvfg">
                    <MailIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1743" data-mp="3dmzr7p4mtn0u" />
                    <input id="bridge-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="yourname@example.com" className="w-full pl-12 pr-4 py-3.5 rounded-lg border-2 border-gray-200 bg-white text-base text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors" data-id="element-1744" data-mp="3ihvmqz92usip" />
                  </div>

                  <button type="submit" disabled={submitting} className="w-full flex items-center justify-center gap-2 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-70" style={{
                backgroundColor: ORANGE
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
              }} data-id="element-1745" data-mp="3sgbd300a5xr5">
                    <span data-id="element-1746" data-mp="36ak0rjatsut3">{submitting ? 'Loading…' : template.ctaLabel}</span>
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1747" data-mp="368h085c1rja4" />
                  </button>

                  {/* Inline trust row */}
                  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-5 text-xs text-gray-600" data-id="element-1748" data-mp="2m8l7siipjx0">
                    <div className="flex items-center gap-1.5" data-id="element-1749" data-mp="3tzcwpfrg72np">
                      <ShieldCheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1750" data-mp="56eodxzb56ci" />
                      <span data-id="element-1751" data-mp="58ho59q9iduf">No credit card</span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1752" data-mp="a5hu7kn43glj">
                      <ZapIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1753" data-mp="f42sheiszq7c" />
                      <span data-id="element-1754" data-mp="j5o3eubs1uew">Instant results</span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1755" data-mp="j3thpdsoly9r">
                      <CheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1756" data-mp="o47q4wy73a8b" />
                      <span data-id="element-1757" data-mp="t3k9v8trs16o">No obligation</span>
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-500 mt-5 leading-relaxed text-center" data-id="element-1758" data-mp="xuaj4x5bgo6n">
                    By clicking, you agree to our{' '}
                    <a href="/terms" className="underline text-gray-700 hover:text-gray-900" data-id="element-1759" data-mp="12t3v2l4yg74p">
                      Terms
                    </a>{' '}
                    &{' '}
                    <a href="/privacy-policy" className="underline text-gray-700 hover:text-gray-900" data-id="element-1760" data-mp="37xgho9zab84l">
                      Privacy Policy
                    </a>{' '}
                    and consent to receive program info via email, phone, or
                    text.
                  </p>
                </form>}
            </aside>
          </div>
        </div>
      </section>

      {/* Content Section — How it works + Testimonials + Trust */}
      <section className="w-full bg-white border-t border-gray-200" data-id="element-1761" data-mp="3mlkvazjhctgi">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-20" data-id="element-1762" data-mp="4grb4nzpbyex">
          {/* How It Works */}
          <div className="text-center mb-12" data-id="element-1763" data-mp="3vnkcpsyepzjz">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4 text-[11px] font-bold tracking-widest" style={{
            backgroundColor: 'rgba(232,89,79,0.1)',
            color: ORANGE
          }} data-id="element-1764" data-mp="3vnu3hira0aqq">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: ORANGE
            }} data-id="element-1765" data-mp="gsaafx9box69"></span>
              HOW IT WORKS
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1766" data-mp="7youialf99v9">
              Your Path to the Right Program in 3 Steps
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1767" data-mp="3u5lwrtmdk7r3">
              We've helped thousands of students find accredited online programs
              that fit their goals, schedule, and budget.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-1768" data-mp="3l71mnguvr6eh">
            {[{
            icon: ClipboardListIcon,
            step: '01',
            title: 'Tell Us About You',
            body: 'Share your goals, education level, and area of interest in under a minute.'
          }, {
            icon: SparklesIcon,
            step: '02',
            title: 'Get Matched Instantly',
            body: 'Our system pairs you with accredited online programs that fit your profile.'
          }, {
            icon: SchoolIcon,
            step: '03',
            title: 'Connect With Schools',
            body: 'Compare programs side-by-side and connect directly with admissions teams.'
          }].map((s, i) => {
            const Icon = s.icon;
            return <div key={i} className="relative bg-gray-50 rounded-2xl p-7 border border-gray-200 hover:border-[#e8594f]/40 hover:shadow-md transition-all" data-id="element-1769" data-mp="3jkkkwhi12w5y" data-mpi={i}>
                  <div className="flex items-center justify-between mb-5" data-id="element-1770" data-mp="39pskz70z3x80">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                  backgroundColor: 'rgba(232,89,79,0.1)'
                }} data-id="element-1771" data-mp="1cueeyzhympqm">
                      <Icon className="w-6 h-6" style={{
                    color: ORANGE
                  }} data-id="element-1772" data-mp="1kf71iagtsjd8" />
                    </div>
                    <span className="text-3xl font-bold text-gray-200" data-id="element-1773" data-mp="1u59uiany5xbk">
                      {s.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1774" data-mp="xgu7al8b801m">
                    {s.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1775" data-mp="16fpbxsvph22t">
                    {s.body}
                  </p>
                </div>;
          })}
          </div>

          {/* Trust band */}
          <div className="mt-16 rounded-2xl p-8 md:p-10 relative overflow-hidden" style={{
          backgroundColor: NAVY
        }} data-id="element-1776" data-mp="2hvdyij51rz33">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1777" data-mp="s4gk5vfeuxzc"></div>
            <div className="relative z-10 grid md:grid-cols-3 gap-6 items-center" data-id="element-1778" data-mp="w5a82osuscs8">
              <div className="flex items-start gap-3" data-id="element-1779" data-mp="idc8b4l17t8q">
                <AwardIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1780" data-mp="ndi2osiortui" />
                <div data-id="element-1781" data-mp="8ik8du3z8uas">
                  <div className="font-bold text-white mb-1" data-id="element-1782" data-mp="df2byqnkmmlj">
                    Only Accredited Schools
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1783" data-mp="3vlyqtzjg5yy6">
                    Every partner school is regionally or nationally accredited.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1784" data-mp="1tu3i9sshmak6">
                <ShieldCheckIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1785" data-mp="1yt6q1kzuquvu" />
                <div data-id="element-1786" data-mp="23xqzy9l2qahg">
                  <div className="font-bold text-white mb-1" data-id="element-1787" data-mp="23yjy4m5yvrnz">
                    Your Privacy Protected
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1788" data-mp="2hq6v6o7qh47t">
                    SSL encrypted. We never sell your data to unrelated third
                    parties.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1789" data-mp="2mo8mtxj86buw">
                <ZapIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1790" data-mp="2mnqlwnlmb9gn" />
                <div data-id="element-1791" data-mp="2wmo5dc46scss">
                  <div className="font-bold text-white mb-1" data-id="element-1792" data-mp="2wk1x1a9h1t9i">
                    Here When You Need Us
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1793" data-mp="128esux4owggg">
                    Explore programs and compare your options at your pace.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full border-t border-gray-200 bg-white" data-id="element-1794" data-mp="3bbcjln8ekxfz">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-700" data-id="element-1795" data-mp="3bd5s782ktrqm">
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs mb-4" data-id="element-1796" data-mp="3gbqudd1e2lhp">
            <a href="/privacy-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1797" data-mp="3kb7yo352xcla">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:text-[#e8594f] transition-colors" data-id="element-1798" data-mp="3u2lu9ztwts95">
              Terms of Use
            </a>
            <a href="/ca-notice" className="hover:text-[#e8594f] transition-colors" data-id="element-1799" data-mp="2uq6v1cqoev1">
              CA Privacy Rights
            </a>
            <a href="/cookie-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1800" data-mp="bwzdvh8stq3o">
              Cookies
            </a>
          </nav>
          <p className="text-xs text-gray-600 mb-1" data-id="element-1801" data-mp="1sbveocivunor">
            © {new Date().getFullYear()} FindOnlineDegrees.org · Media Matchers
            LLC · All Rights Reserved.
          </p>
          <p className="text-[11px] text-gray-500 max-w-2xl mx-auto leading-relaxed" data-id="element-1802" data-mp="1iegyme0vg0wz">
            FindOnlineDegrees.org is a service that helps students explore
            accredited online degree programs. We may receive compensation from
            school partners when users inquire.
          </p>
        </div>
      </footer>
    </div>;
}